/**
 * Overlay primitives (ADR-061) — replaces the `Dialog` family (64 + 63 +
 * 62 + 61 imports), `Tooltip` (69) and MUI `Menu`.
 *
 * DIALOG USES THE NATIVE `<dialog>` ELEMENT. Focus trapping, the
 * top-layer stacking that no z-index can lose a fight with, `Escape` to
 * close, inert background content and `::backdrop` all come from the
 * platform. Every one of those is something a div-based modal has to
 * reimplement, usually incompletely — and an incompletely trapped modal
 * on a clinical screen means a clerk tabs into the form behind it and
 * edits the wrong record.
 *
 * TOOLTIP USES RADIX, which is already a dependency. A tooltip needs
 * collision-aware positioning, hover/focus intent delays and correct
 * `aria-describedby` wiring; hand-rolling that is not a saving. A
 * tooltip is never the only carrier of information — it must always
 * duplicate something already visible or reachable another way, because
 * touch users have no hover.
 *
 * POPOVER USES RADIX TOO, for the identical reason: an anchored floating
 * panel needs collision-aware positioning, focus management and
 * outside-click/Escape dismissal, and a hand-rolled version of any of those
 * is exactly the "incompletely trapped" risk `Dialog`'s own header warns
 * about — just for a panel rather than a modal. Replaces MUI's `Popover`
 * (desk, 2026-09-29: the notification bell was the last MUI holdout here).
 */
import { useCallback, useEffect, useId, useRef, type JSX, type ReactNode } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import * as RadixPopover from '@radix-ui/react-popover';
import { cn } from '@utils/cn';
import { IconButton } from './primitives';

/* ------------------------------------------------------------------ *
 * Dialog
 * ------------------------------------------------------------------ */

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Sub-heading under the title, for context the title cannot carry. */
  description?: string;
  children: ReactNode;
  /** Right-aligned action row, pinned to the bottom. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Escape hatch for a dialog whose width is a PROPORTION of the viewport
   * rather than one of the four steps — a grid of 50 numbered slots across
   * seven days needs the room it needs, and the nearest fixed step either
   * wastes half a 1080p screen or clips the grid on a 1366px laptop.
   *
   * Applied last, so it wins over `DIALOG_WIDTH[size]`. Reach for `size`
   * first: four steps is what keeps dialogs across the app looking like
   * one family.
   */
  panelClassName?: string;
  /**
   * Blocks backdrop-click and Escape. Use ONLY where dismissing would
   * lose work or leave a half-finished clinical record — never as a
   * default, since a modal a user cannot escape is a trap.
   */
  dismissible?: boolean;
  /**
   * `center` (default) is every ordinary dialog — a focused task the user
   * came here to do. `top` is for a NOTICE about something happening TO the
   * screen rather than something the user asked to open (the system-update
   * banner) — still the same native `<dialog>` underneath, so it keeps
   * `showModal()`'s focus trap and inert background, just anchored near the
   * top of the viewport instead of dead centre (desk, 2026-09-29: "system
   * update not required in center").
   */
  position?: 'center' | 'top';
}

/**
 * `xl` widened from `max-w-5xl` (64rem) to `max-w-7xl` (80rem). It is the size
 * the BILL viewer asks for, and an A5-landscape invoice rendered inside 64rem
 * left the particulars column squeezed against the amount column on a screen
 * with room to spare either side.
 */
const DIALOG_WIDTH = { sm: 'tw:max-w-sm', md: 'tw:max-w-lg', lg: 'tw:max-w-3xl', xl: 'tw:max-w-7xl' };

/**
 * Overrides the UA stylesheet's `dialog:modal { margin: auto }` (full
 * centring) with an explicit top margin, leaving the horizontal centring
 * alone. No `!important` needed: an author rule beats a user-agent rule by
 * ORIGIN before specificity is even compared, so a plain utility class wins
 * outright. The dialog's own focus-trap/inert/top-layer behaviour comes from
 * `showModal()` itself and is unaffected by where the box sits.
 */
const DIALOG_POSITION = { center: '', top: 'tw:mx-auto tw:mt-8 tw:mb-auto' };

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  panelClassName,
  dismissible = true,
  position = 'center',
}: DialogProps): JSX.Element | null {
  const ref = useRef<HTMLDialogElement>(null);
  /**
   * A unique id for THIS dialog's title. It used to be the literal "nx-dialog-title", so with two dialogs
   * open (a form opened over a sheet) both `aria-labelledby` pointed at whichever title came first in the
   * page, and a screen reader announced the parent's name for the child. One id per dialog fixes that.
   */
  const titleId = useId();
  /**
   * True while WE are closing the dialog because `open` went false.
   *
   * THE DEFECT THIS FIXES. `<dialog>` fires a native `close` event whenever it
   * closes — including from `element.close()` below — and that event was wired
   * straight to `onClose`. So a dialog dismissed by its OWN action ran the
   * parent's dismissal handler as well.
   *
   * On the billing desk that was a real loss of work: the admitted-patient
   * warning's `onClose` clears the selected patient (the right thing when the
   * operator backs out), so pressing "Continue as an OP bill" closed the
   * dialog, fired `close`, and wiped the patient the operator had just chosen
   * to keep. The desk saw the warning accept and the bill refuse to start.
   *
   * `onClose` now means what its name says: THE USER DISMISSED THIS. A close
   * driven by the `open` prop is the parent's own decision and needs no
   * callback — the parent already knows.
   */
  const closingFromProp = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    // `showModal()` (not `show()`) is what puts the dialog in the top
    // layer and makes the rest of the document inert. Guarded because a
    // browser without `<dialog>` support should degrade to an inline
    // panel rather than throw on every open.
    if (open && !element.open) {
      if (typeof element.showModal === 'function') element.showModal();
      else element.setAttribute('open', '');
    }
    if (!open && element.open) {
      closingFromProp.current = true;
      if (typeof element.close === 'function') element.close();
      else element.removeAttribute('open');
    }
  }, [open]);

  /**
   * The native `close` event, filtered down to user dismissal.
   *
   * Escape and backdrop clicks reach `onClose` through their own handlers and
   * then close via the `open` prop, so swallowing the programmatic event does
   * not lose them — it stops each dismissal being reported twice.
   */
  const handleNativeClose = useCallback(() => {
    if (closingFromProp.current) {
      closingFromProp.current = false;
      return;
    }
    onClose();
  }, [onClose]);

  const handleCancel = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      // `cancel` fires on Escape. Preventing it is how a non-dismissible
      // dialog stays open without us binding a global key handler.
      event.preventDefault();
      if (dismissible) onClose();
    },
    [dismissible, onClose],
  );

  return (
    <dialog
      ref={ref}
      onCancel={handleCancel}
      onClose={handleNativeClose}
      /*
        NO BACKDROP-CLICK CLOSE (desk, 2026-09-06), matching the rule now
        applied to every MUI dialog in `theme/components.ts`.

        This used to close whenever a click landed on the `<dialog>` element
        itself rather than on its content — which is exactly what a click
        outside the panel is. A dialog here routinely holds minutes of typing,
        and losing that to a stray click is not a trade worth the convenience
        of dismissing by tapping away.

        `dismissible` still governs Escape and the header's close button, so a
        dialog that should be hard to leave still is. What changed is only that
        the EASIEST accident no longer discards work.
      */
      aria-labelledby={titleId}
      className={cn(
        'nx-root tw:w-[92vw] tw:rounded-lg tw:border tw:border-nx-border tw:bg-nx-surface tw:p-0 tw:text-nx-text tw:shadow-xl',
        'backdrop:tw:bg-black/40',
        DIALOG_WIDTH[size],
        DIALOG_POSITION[position],
        panelClassName,
      )}
    >
      {open && (
        <div className="tw:flex tw:max-h-[85vh] tw:flex-col">
          <div className="tw:flex tw:items-start tw:gap-2 tw:border-b tw:border-nx-border tw:px-3 tw:py-2">
            <div className="tw:min-w-0 tw:flex-1">
              <h2 id={titleId} className="tw:text-sm tw:font-semibold tw:text-nx-text">
                {title}
              </h2>
              {description && <p className="tw:mt-0.5 tw:text-label tw:text-nx-text-muted">{description}</p>}
            </div>
            {/* An X, at the top right, on every dialog in the app.
                It was `icon="error"` — an exclamation mark in a circle, which
                reads as "something went wrong", not "close this". The glyph is
                the only thing a user looks for in that corner, and the wrong
                one there is worse than none. `close` is Google's own X. */}
            {dismissible && <IconButton icon="close" label="Close" size="sm" onClick={onClose} />}
          </div>
          <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:p-3">{children}</div>
          {footer && (
            <div className="tw:flex tw:items-center tw:justify-end tw:gap-2 tw:border-t tw:border-nx-border tw:px-3 tw:py-2">{footer}</div>
          )}
        </div>
      )}
    </dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Tooltip
 * ------------------------------------------------------------------ */

/**
 * Optional. Mount high in the tree to get Radix's shared "skip delay"
 * grouping — once one tooltip has opened, neighbours open instantly.
 *
 * It is NOT required: `Tooltip` provides its own provider (see below).
 * A component that throws unless a caller remembered to mount something
 * is a trap, and this codebase has already been bitten once by exactly
 * that shape of dependency.
 */
export function TooltipProvider({ children }: { children: ReactNode }): JSX.Element {
  return (
    <RadixTooltip.Provider delayDuration={300} skipDelayDuration={150}>
      {children}
    </RadixTooltip.Provider>
  );
}

export function Tooltip({
  content,
  children,
  side = 'top',
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}): JSX.Element {
  if (!content) return <>{children}</>;
  return (
    // Self-providing. Radix throws "`Tooltip` must be used within
    // `TooltipProvider`" otherwise, which turns a missing provider —
    // easy to forget on any of 500+ screens — into a white-screen crash
    // rather than a degraded tooltip. Nesting inside an outer provider
    // is supported; the outer one still governs shared delay grouping.
    <RadixTooltip.Provider delayDuration={300} skipDelayDuration={150}>
      <RadixTooltip.Root>
      {/* `asChild` so the trigger is the child element itself, not an
          extra span that would break grid/flex layout around it. */}
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          sideOffset={4}
          collisionPadding={8}
          className="nx-root tw:z-[1300] tw:max-w-xs tw:rounded tw:border tw:border-nx-border tw:bg-nx-surface tw:px-2 tw:py-1 tw:text-label tw:text-nx-text tw:shadow-md"
        >
          {content}
          <RadixTooltip.Arrow className="tw:fill-[var(--nx-border)]" />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}

/* ------------------------------------------------------------------ *
 * Popover — replaces MUI's anchored `Popover`
 * ------------------------------------------------------------------ */

export interface PopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The element that opens the panel. `asChild`'d onto Radix's trigger — must accept a ref and forward it. */
  trigger: ReactNode;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  /** Extra classes on the floating panel — width/max-height belong here, not on `children`. */
  className?: string;
}

export function Popover({ open, onOpenChange, trigger, children, align = 'end', className }: PopoverProps): JSX.Element {
  return (
    <RadixPopover.Root open={open} onOpenChange={onOpenChange}>
      {/* `asChild` so the trigger is the button itself, not an extra
          wrapping element that would break layout around it — the same
          reason `Tooltip.Trigger` above does it. */}
      <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          align={align}
          sideOffset={4}
          collisionPadding={8}
          className={cn(
            'nx-root tw:z-[1300] tw:overflow-hidden tw:rounded-lg tw:border tw:border-nx-border tw:bg-nx-surface tw:text-nx-text tw:shadow-xl tw:outline-none',
            className,
          )}
        >
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
